/**
 * money — shared paise formatter + IST-safe service-date labels for the IPD
 * charge ledger (EPIC C / C3).
 *
 * The C3 API returns ALL money as INTEGER PAISE (signed) — `amount_paise`,
 * `balance_paise`, `unit_price_paise`, … — so the ledger never round-trips a
 * float/Decimal. `formatPaise` is the ONE place that turns paise into a rupee
 * string; no component formats paise inline.
 *
 * `service_date` is a plain "YYYY-MM-DD" org-local calendar string. It is
 * formatted by splitting the string and constructing a LOCAL Date — never via
 * `toISOString()`, which would shift the day across the IST/UTC midnight boundary
 * (the known IST/UTC day-bucket trap).
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format signed integer paise as an Indian-grouped rupee string.
 *   150000  → "₹1,500"           (whole rupees → no decimals, matches OPD UI)
 *   47250   → "₹472.50"          (fractional → 2 decimals)
 *   -120000 → "−₹1,200"          (negative → leading minus)
 * A nullish / non-numeric input formats as "₹0" (never throws) — but callers
 * must pass the real field, never a fake ₹0 stand-in for missing data.
 */
export function formatPaise(paise) {
  const n = Math.round(Number(paise) || 0);
  const negative = n < 0;
  const rupees = Math.abs(n) / 100;
  const grouped = rupees.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(rupees) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `${negative ? '−' : ''}₹${grouped}`;
}

/**
 * Format a deduction MAGNITUDE (Discounts/Refunds columns on the monthly
 * cash-collections tables — §API CONTRACT: these wire fields are always
 * >= 0) as a minus-prefixed rupee string:
 *   120000  → "−₹1,200"
 *   0       → "₹0"        (never a signed "−₹0" — P2 QA tuning)
 * The ONE place that decides whether a deduction cell gets a minus sign —
 * `CashMemberTable`'s row AND footer cells both call this, so the zero rule
 * can't drift between them.
 */
export function formatDeduction(paise) {
  const n = Math.round(Number(paise) || 0);
  if (n === 0) return formatPaise(0);
  return `−${formatPaise(Math.abs(n))}`;
}

/**
 * Format a RUPEE (not paise) amount as an Indian-grouped ₹ string — no
 * decimals when whole, 2 decimals when the amount carries paise. Mirrors
 * `formatPaise` for the rupee-unit side of the charge-items catalog
 * (Price/Suggested-price/Varies cells) and the IPD add-charge dialog's
 * range-bound hints — ONE formatter so the two surfaces can't drift.
 *   1200   → "₹1,200"
 *   1200.5 → "₹1,200.50"
 */
export function formatRupees(n) {
  const num = Number(n) || 0;
  const grouped = num.toLocaleString('en-IN', {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return `₹${grouped}`;
}

function splitYmd(ymd) {
  if (!ymd || typeof ymd !== 'string') return null;
  const parts = ymd.split('-').map((p) => Number(p));
  const [y, m, d] = parts;
  if (!y || !m || !d || parts.length !== 3) return null;
  const dt = new Date(y, m - 1, d); // LOCAL construction — no UTC day shift
  if (Number.isNaN(dt.getTime())) return null;
  return { y, m, d, dt };
}

/** "YYYY-MM-DD" → "Sat, 12 Jul 2026" (IST-safe day-group header). */
export function formatServiceDate(ymd) {
  const p = splitYmd(ymd);
  if (!p) return '';
  return `${WEEKDAYS[p.dt.getDay()]}, ${p.d} ${MONTHS[p.m - 1]} ${p.y}`;
}

/** "YYYY-MM-DD" → "9 Jul" (compact reference inside a reversal subtitle). */
export function formatServiceDateShort(ymd) {
  const p = splitYmd(ymd);
  if (!p) return '';
  return `${p.d} ${MONTHS[p.m - 1]}`;
}

/** Today's org-local calendar date as "YYYY-MM-DD" (default service-date). */
export function todayYmd(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * ISO "…T…" timestamp → "14 Jul 2026" (date only; org-local, already IST on
 * the wire). Shared by the final-bill panel (bill dates) and the C6b dues
 * lifecycle states (`dues_settled_at` / `dues_written_off_at`) — ONE date
 * formatter so the two surfaces can't drift on format. Nullish/invalid → ''.
 */
export function formatIsoDateOnly(iso) {
  if (!iso || typeof iso !== 'string') return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * "YYYY-MM-DD" (or any Date-parseable string) → "dd/mm/yyyy" — the ONE
 * settlement-date format shared by every settled-credit chip (IPD/OPD combined
 * ledgers reuse this instead of re-deriving their own format; drift caught the
 * hard way when ManageVisitPage's IPD-lab settlement chip and the credits
 * ledger printed two different date shapes for the SAME stored
 * `payment_cleared_at`/`settled_at` field). Missing/invalid input degrades to
 * '' so a chip never prints "Invalid Date".
 */
export function formatSettlementDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr.length <= 10 ? `${dateStr}T00:00:00` : dateStr);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getFullYear()}`;
}
